import type { StatusItem } from './kpi.types';

const toNumber = (value: unknown): number | undefined => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }
    return undefined;
};

export const formatValue = (value: number | undefined) => {
    if (value === undefined) {
        return '0';
    }
    return String(Number(value.toFixed(1)));
};

export const formatPercentage = (value: number | undefined) => {
    const safeValue = Number.isFinite(value as number) ? Number(value) : 0;
    return safeValue.toFixed(1);
};

const roundPercentage = (value: number) => Number(formatPercentage(value));

const getMetricNumber = (source: Record<string, unknown> | undefined, key: string) => {
    return toNumber(source?.[key]) ?? 0;
};

const sumMetricNumbersByKeyMatcher = (
    source: Record<string, unknown> | undefined,
    matcher: (key: string) => boolean,
) => {
    if (!source) return 0;

    return Object.entries(source).reduce((sum, [key, value]) => {
        if (!matcher(key)) return sum;
        return sum + (toNumber(value) ?? 0);
    }, 0);
};

export const buildSplotStatusData = (metricsData: any): StatusItem[] => {
    const statusCounts = (metricsData?.splot_status_count ?? {}) as Record<string, unknown>;
    const statusPercentages = (metricsData?.splot_status_percentage ?? {}) as Record<string, unknown>;
    const totalSplots = toNumber(metricsData?.total_splots) ?? 0;

    const formatCount = (count: number) => {
        return totalSplots > 0 ? `${count}/${totalSplots}` : `${count}`;
    };

    const seedExtractionFermentationCount =
        getMetricNumber(statusCounts, 'Seed Extraction') +
        getMetricNumber(statusCounts, 'Fermentation');
    const seedExtractionFermentationPercent =
        getMetricNumber(statusPercentages, 'Seed Extraction') +
        getMetricNumber(statusPercentages, 'Fermentation');

    const disinfectionCentrifugingCount =
        sumMetricNumbersByKeyMatcher(statusCounts, (key) => /^disinfection([_\s-]?\d+)?$/i.test(key.trim())) +
        getMetricNumber(statusCounts, 'Centrifuging');
    const disinfectionCentrifugingPercent =
        sumMetricNumbersByKeyMatcher(statusPercentages, (key) => /^disinfection([_\s-]?\d+)?$/i.test(key.trim())) +
        getMetricNumber(statusPercentages, 'Centrifuging');

    const combinedCleaningCount =
        getMetricNumber(statusCounts, 'Blower Cleaning') +
        getMetricNumber(statusCounts, 'Manual Cleaning') +
        getMetricNumber(statusCounts, 'Cleaning');
    const combinedCleaningPercent =
        getMetricNumber(statusPercentages, 'Blower Cleaning') +
        getMetricNumber(statusPercentages, 'Manual Cleaning') +
        getMetricNumber(statusPercentages, 'Cleaning');

    const statusData = [
        {
            label: 'Created',
            count: formatCount(getMetricNumber(statusCounts, 'Created')),
            percent: getMetricNumber(statusPercentages, 'Created'),
            color: '#76DAB5',
        },
        {
            label: 'Seed Extraction & Fermentation',
            count: formatCount(seedExtractionFermentationCount),
            percent: seedExtractionFermentationPercent,
            color: '#BAD1FA',
        },
        {
            label: 'Disinfection & Centrifuging',
            count: formatCount(disinfectionCentrifugingCount),
            percent: disinfectionCentrifugingPercent,
            color: '#628DF6',
        },
        {
            label: 'Drying',
            count: formatCount(getMetricNumber(statusCounts, 'Drying')),
            percent: getMetricNumber(statusPercentages, 'Drying'),
            color: '#D197FA',
        },
        {
            label: 'Cleaning',
            count: formatCount(combinedCleaningCount),
            percent: combinedCleaningPercent,
            color: '#B5BEC8',
        },
        {
            label: 'Packaging & Labelling',
            count: formatCount(getMetricNumber(statusCounts, 'Packaging & Labelling')),
            percent: getMetricNumber(statusPercentages, 'Packaging & Labelling'),
            color: '#14B8A6',
        },
        {
            label: 'MINT Registration',
            count: formatCount(getMetricNumber(statusCounts, 'Mint Registration')),
            percent: getMetricNumber(statusPercentages, 'Mint Registration'),
            color: '#F58BC4',
        },
        {
            label: 'Heat Treatment',
            count: formatCount(getMetricNumber(statusCounts, 'Heat Treatment')),
            percent: getMetricNumber(statusPercentages, 'Heat Treatment'),
            color: '#EF4444',
        },
        {
            label: 'Completed',
            count: formatCount(getMetricNumber(statusCounts, 'Completed')),
            percent: getMetricNumber(statusPercentages, 'Completed'),
            color: '#FFE233',
        },
    ].map((item) => ({
        ...item,
        percent: roundPercentage(item.percent),
    }));

    // Normalize percentages to sum to 100 to prevent gaps in donut chart
    const totalPercent = statusData.reduce((sum, item) => sum + item.percent, 0);
    if (totalPercent > 0 && totalPercent !== 100) {
        const scale = 100 / totalPercent;
        return statusData.map(item => ({
            ...item,
            percent: roundPercentage(item.percent * scale),
        }));
    }

    return statusData;
};

export const buildTrailStatusData = (metricsData: any): StatusItem[] => {
    const statusCounts = (metricsData?.processing_status_count ?? {}) as Record<string, unknown>;
    const statusPercentages = (metricsData?.processing_status_percentage ?? {}) as Record<string, unknown>;
    const totalProcessingMaterials = toNumber(metricsData?.total_processing_materials) ?? toNumber(metricsData?.total_materials) ?? 0;

    const formatCount = (count: number) => {
        return totalProcessingMaterials > 0 ? `${count}/${totalProcessingMaterials}` : `${count}`;
    };

    const statusData = [
        {
            label: 'Completed after 1st harvest',
            count: formatCount(getMetricNumber(statusCounts, 'Completed after 1st harvest')),
            percent: getMetricNumber(statusPercentages, 'Completed after 1st harvest'),
            color: '#FFE233',
        },
        {
            label: 'Completed after 2nd harvest',
            count: formatCount(getMetricNumber(statusCounts, 'Completed after 2nd harvest')),
            percent: getMetricNumber(statusPercentages, 'Completed after 2nd harvest'),
            color: '#76DAB5',
        },
         {
            label: 'Completed after multiple harvest',
            count: formatCount(getMetricNumber(statusCounts, 'Completed after multiple harvest')),
            percent: getMetricNumber(statusPercentages, 'Completed after multiple harvest'),
            color: '#48BAFA',
        },
        {
            label: 'Not Harvested',
            count: formatCount(getMetricNumber(statusCounts, 'Not harvested')),
            percent: getMetricNumber(statusPercentages, 'Not harvested'),
            color: '#BAD1FA',
        },
        {
            label: 'In Progress',
            count: formatCount(getMetricNumber(statusCounts, 'In progress')),
            percent: getMetricNumber(statusPercentages, 'In progress'),
            color: '#D197FA',
        },
    ].map((item) => ({
        ...item,
        percent: roundPercentage(item.percent),
    }));

    // Normalize percentages to sum to 100 to prevent gaps in donut chart
    const totalPercent = statusData.reduce((sum, item) => sum + item.percent, 0);
    if (totalPercent > 0 && totalPercent !== 100) {
        const scale = 100 / totalPercent;
        return statusData.map(item => ({
            ...item,
            percent: roundPercentage(item.percent * scale),
        }));
    }

    return statusData;
};

export const readMetricNumber = (value: unknown): number | undefined => toNumber(value);
